import { LoaderFunctionArgs, useLoaderData, useParams, useSearchParams } from "react-router";
import { CommunityChannelMessage, type Embed, fetchCommunityChannelMessages, type CommunityChannel, fetchCommunityChannelList } from "#lib/api/community-channels";
import { Timestamp } from "#components/dates";
import { PageSkeleton } from "#components/pages";
import { createProfilePageURL } from "#lib/urls";
import { KemonoLink } from "#components/links";

import * as styles from "./community-channel.module.scss";

function Embed({ embed }: { embed: Embed }) {
  function rewriteLink(s: string | undefined): string | undefined {
    return s?.replace(/(?:https?:\/\/(?:www\.)?)?patreon\.com\/posts\/(\d+)(?:\?.+)?/, "/patreon/post/$1")
             .replace(/(?:https?:\/\/(?:www\.)?)?patreon\.com\/posts\/(?:[\w\-]+)\-(\d+)(?:\?.+)?/, "/patreon/post/$1");
  }

  if (embed.type) {
    return (
      <div className={styles.embed}>
        <a href={rewriteLink(embed.asset_url ?? embed.title_link)} target="_blank">
          <div>
            <picture>
              <source srcSet={embed.image_url} />
              <img src={embed.thumb_url} loading="lazy" />
            </picture>
            <p title={embed.text}>{embed.title}</p>
          </div>
        </a>
      </div>
    );
  } else {
    return <>Invalid embed!</>;
  }
}

function Message({ message }: { message: CommunityChannelMessage }) {
  return (
    <div key={message.message_id} id={message.message_id} className={styles.messageContainer}>
      <div className={styles.message}>
        <div className={styles.messageContent}>
          <div className={styles.body}>
            {message.deleted_at ? (
              <div className={styles.deleted}>Message deleted</div>
            ) : (
              <>
                <header>
                  <strong className={styles.userName} title={message.original_json.user.name}>
                    {message.original_json.user.name}
                  </strong>
                  {message.original_json.user.role != "user" ? (
                    <strong className={styles.creatorBadge}>
                      {{ campaign: "CREATOR", admin: "ADMIN", patreon_admin: "PATREON" }[message.original_json.user.role]}
                    </strong>
                  ) : undefined}
                  <span>{"  ⸱  "}</span>
                  <Timestamp time={message.original_json.created_at} isRelative />
                </header>
                <main>
                  <div className={styles.text}>
                    {message.original_json.text}
                  </div>
                  <div className={styles.embeds}>
                    {message.embeds.map(embed => <Embed embed={embed} />)}
                  </div>
                  <div className={styles.attachments}>
                    {message.attachments.map(attachment => (
                      <a href={`https://kemono.su/data${attachment.path}`} className={styles.attachment}>
                        <img src={`https://kemono.su/thumbnail/data${attachment.path}`} />
                      </a>
                    ))}
                  </div>
                </main>
              </>
            )}
          </div>
          {message.replies?.length ? (
            <div className={styles.replyContainer}>
              <details className={styles.replyContainer}>
                <summary>{message.replies.length} {message.replies.length == 1 ? "Reply" : "Replies"}</summary>
                {message.replies.map(reply => <Message message={reply} />)}
              </details>
            </div>
          ) : undefined}
        </div>
      </div>
    </div>
  )
}

interface IProps {
  channel: CommunityChannel;
  messages: CommunityChannelMessage[];
  offset: number;
}

export function CommunityChannel() {
  const { channel, messages, offset } = useLoaderData<IProps>();
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const creatorId = params.creatorId!;

  // DIY state because apparently React-Router doesn't work right.
  function setOffset(o: number) {
    setSearch(s => {
      if (o > 0) {
        s.set("o", o.toString());
      } else {
        s.delete("o");
      }
      return s;
    });
  }

  return (
    <PageSkeleton name="community-channel" title={channel.name}>
      <div>
        <header className={styles.header}>
          <KemonoLink url={String(createProfilePageURL({ service: "patreon", profileID: creatorId }))}>« Back</KemonoLink>
          <div>Messages {offset}—{offset + messages.length} for {channel.name}</div>
          <div>
            <button onClick={() => setOffset(offset + 150)} disabled={messages.length != 150} className="button">Older</button>
            <button onClick={() => setOffset(offset - 150)} disabled={offset == 0} className="button" style={{marginLeft: "8px"}}>Newer</button>
          </div>
        </header>
        {messages.map(message => <Message message={message} />)}
      </div>
    </PageSkeleton>
  );
}

export async function loader({ params, request }: LoaderFunctionArgs): Promise<IProps> {
  const channelId = params.channelId!;
  const creatorId = params.creatorId!;
  const offset = Number(new URL(request.url).searchParams.get("o")) || 0;
  const messages = fetchCommunityChannelMessages(channelId, offset);
  const channel = (await fetchCommunityChannelList(creatorId)).find(c => {
    return c.channel_id == channelId;
  });

  if (!channel) {
    throw new Error("Invalid channel ID");
  }

  return {
    channel: channel,
    messages: (await messages).reverse(),
    offset,
  };
}
